"use client";

import React, { useEffect, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";

import {
  Search,
  Mic,
  Plus,
  Trash2,
  Pill,
  FlaskConical,
  BadgeCheck,
  FileText,
  RefreshCw,
  Copy,
  X,
} from "lucide-react";

import "./TreatmentPlan.css";
import nomenclature from "../../FilePage/AddNewConsultationZone/nomenclature.json";
import CnamBookModal from "../../../../Profile/components/ProfilePage/CnamBookModal/CnamBookModal";
import { fetchMedications } from "../../../../medications/redux/thunks/medicationThunks";
import { fetchPrescriptionsByPatientForDoctor } from "../../../../prescriptions/redux/thunks/prescriptionThunks";

/* =========================================================
   DOCUMENT TYPES
========================================================= */

const DOCUMENT_TYPES = [
  {
    key: "prescription",
    label: "Prescription",
    icon: Pill,
  },
  {
    key: "bilan",
    label: "Lab / Test Request",
    icon: FlaskConical,
  },
  {
    key: "radio",
    label: "Certificate",
    icon: BadgeCheck,
  },
  {
    key: "lettre",
    label: "Referral Letter",
    icon: FileText,
  },
];

/* =========================================================
   MEDICAL ACTS
========================================================= */

const resolveDoctorKc = (source = "") => {
  const raw = String(source || "").trim();

  if (!raw) {
    return 1;
  }

  let hash = 0;
  for (let index = 0; index < raw.length; index += 1) {
    hash = (hash * 31 + raw.charCodeAt(index)) >>> 0;
  }

  return ((hash % 3) + 1);
};

/*
 * Coefficient KC d'un acte : valeur numérique > 0, sinon 1.
 * (l'input peut temporairement contenir "" ou "0" pendant la saisie)
 */
const getActKey = (act) => {
  const value = Number(String(act?.key ?? "").replace(",", "."));

  return Number.isFinite(value) && value > 0 ? value : 1;
};

/* Montant d'un acte = KC x cotation */
const getActAmount = (act) =>
  getActKey(act) * Number(act?.cotation || 0);

const MEDICAL_ACTS = nomenclature
  .filter((act) => act?.code && act?.designation)
  .map((act) => ({
    id: act.code,
    code: act.code,
    designation: act.designation,
    key: null,
    keyLabel: act.cle || "KC",
    cotation: Number(act.cotation) || 0,
  }));

/* =========================================================
   LAB TESTS
========================================================= */

const LAB_TEST_OPTIONS = [
  {
    id: "creatinine_dfg",
    label: "Créatinine + DFG",
  },
  {
    id: "nfs",
    label: "NFS",
  },
  {
    id: "ionogramme",
    label: "Ionogramme sanguin",
  },
  {
    id: "hba1c",
    label: "HbA1c",
  },
  {
    id: "bilan_hepatique",
    label: "Bilan hépatique (ASAT/ALAT/GGT)",
  },
  {
    id: "bilan_lipidique",
    label: "Bilan lipidique",
  },
  {
    id: "vitamine_d",
    label: "Vitamine D",
  },
  {
    id: "gaz_sang",
    label: "Gaz du sang artériel",
  },
  {
    id: "ferritine",
    label: "Ferritine",
  },
  {
    id: "psa_total",
    label: "PSA total",
  },
  {
    id: "tsh",
    label: "TSH",
  },
  {
    id: "crp",
    label: "CRP",
  },
];

/* =========================================================
   EMPTY DOCUMENTS
========================================================= */

const EMPTY_DOCUMENTS = {
  prescription: "",
  bilan: "",
  radio: "",
  lettre: "",
};

const hasCnamCoverage = (value) => {
  if (value == null) return false;
  if (typeof value === "string") {
    return value.trim().toUpperCase().includes("CNAM");
  }
  if (Array.isArray(value)) {
    return value.some((item) => hasCnamCoverage(item));
  }
  if (typeof value === "object") {
    return Object.values(value).some((item) => hasCnamCoverage(item));
  }
  return false;
};

const documentTextsAreEqual = (first, second) =>
  Object.keys(EMPTY_DOCUMENTS).every(
    (key) => String(first?.[key] || "") === String(second?.[key] || "")
  );

/* =========================================================
   NORMALIZE CURRENT TREATMENT
========================================================= */

const normalizeTreatment = (item, index) => {
  if (!item) return null;

  if (typeof item === "string") {
    const name = item.trim();

    if (!name) return null;

    return {
      id: `renewed-${index}-${Date.now()}`,
      name,
      dosage: "",
      renewed: true,
    };
  }

  const nestedMedication =
    item?.medication && typeof item.medication === "object"
      ? item.medication
      : null;

  const name =
    item?.medicationName ||
    item?.drugName ||
    item?.medicineName ||
    item?.productName ||
    item?.name ||
    item?.label ||
    nestedMedication?.name ||
    nestedMedication?.medicationName ||
    "";

  if (!String(name || "").trim()) {
    return null;
  }

  const dose =
    item?.dosage ||
    item?.dose ||
    nestedMedication?.dosage ||
    nestedMedication?.dose ||
    "";

  const frequency =
    item?.frequency ||
    item?.frequence ||
    nestedMedication?.frequency ||
    "";

  const duration =
    item?.duration ||
    item?.duree ||
    nestedMedication?.duration ||
    "";

  const dosageParts = [dose, frequency, duration]
    .map((value) => String(value || "").trim())
    .filter(Boolean);

  return {
    id:
      item?.id ||
      item?._id ||
      item?.medicationId ||
      `renewed-${index}-${Date.now()}`,

    name: String(name).trim(),

    dosage: dosageParts.join(" • "),

    renewed: true,
  };
};

/* =========================================================
   COMPONENT
========================================================= */

export default function TreatmentPlan({
  onSavePrescription,
  onDocumentTextsChange,
  onPreviewChange,
  onDocKeyChange,
  onGenerateDocument,
  onClearGeneratedDocument,
  onMedicalActsChange,
  onConsultationPriceChange,

  initialDocumentTexts = null,
  initialDocumentKey = null,

  patientData,
  patientId,
  doctorId,
  insuranceData,
  currentUser,
  doctorInfo,
  emergencyData,

  currentTreatments = null,

  frontOnly = false,
}) {
  const dispatch = useDispatch();
  const { t } = useTranslation();

  /* =======================================================
     ACTIVE DOCUMENT
  ======================================================= */

  const [selectedDocKey, setSelectedDocKey] =
    useState(initialDocumentKey || "prescription");

  /* =======================================================
     DOCUMENT TEXTS
  ======================================================= */

  const [documentTexts, setDocumentTexts] = useState({
    ...EMPTY_DOCUMENTS,
    ...(initialDocumentTexts || {}),
  });

  /* =======================================================
     MEDICAL ACTS
  ======================================================= */

  const [actQuery, setActQuery] = useState("");

  const [selectedActs, setSelectedActs] = useState([]);

  /*
   * KC par défaut du médecin (valeur initiale proposée pour chaque acte).
   * Le médecin peut ensuite le modifier acte par acte (1, 2, 3, ...).
   */
  const doctorKc = useMemo(() => {
    const explicitCandidate = [
      doctorInfo?.kc,
      doctorInfo?.key,
      doctorInfo?.coefficient,
      doctorInfo?.doctorKc,
      doctorInfo?.doctorCoefficient,
      currentUser?.kc,
      currentUser?.key,
      currentUser?.coefficient,
      currentUser?.doctorKc,
      currentUser?.doctorCoefficient,
    ].find((value) => Number.isFinite(Number(value)) && Number(value) > 0);

    if (explicitCandidate != null) {
      const numericValue = Number(explicitCandidate);
      if (Number.isFinite(numericValue) && numericValue > 0) {
        return numericValue;
      }
    }

    const doctorSource = [
      doctorInfo?.id,
      doctorInfo?._id,
      doctorInfo?.pgId,
      doctorInfo?.doctorId,
      currentUser?.id,
      currentUser?._id,
      currentUser?.pgId,
      currentUser?.doctorId,
    ].filter(Boolean).join("|");

    return resolveDoctorKc(doctorSource);
  }, [currentUser, doctorInfo]);

  const actCatalog = useMemo(
    () =>
      MEDICAL_ACTS.map((act) => ({
        ...act,
        key: doctorKc,
      })),
    [doctorKc]
  );

  const [consultationPrice, setConsultationPrice] = useState(70);

  const [focusedActSearch, setFocusedActSearch] =
    useState(false);

  /* =======================================================
     PRESCRIPTION
  ======================================================= */

  const [medicationName, setMedicationName] =
    useState("");

  const [medicationDosage, setMedicationDosage] =
    useState("");

  const [isCnamBookOpen, setIsCnamBookOpen] = useState(false);

  const doctorInsurance = [
    doctorInfo,
    currentUser,
    doctorInfo?.insurances,
    doctorInfo?.acceptedInsurance,
    doctorInfo?.insuranceCoverage,
    doctorInfo?.insurance,
    currentUser?.insurances,
    currentUser?.acceptedInsurance,
    currentUser?.insuranceCoverage,
    currentUser?.insurance,
  ];

  const patientHasCnam = hasCnamCoverage([
    insuranceData,
    insuranceData?.provider,
    insuranceData?.insurances,
    insuranceData?.insurance,
    patientData?.insurance,
    patientData?.insurances,
  ]);

  const doctorHasCnam = hasCnamCoverage(doctorInsurance);

  const shouldDisplayInsuranceCard =
    patientHasCnam && doctorHasCnam;

  if (import.meta.env.DEV) {
    console.debug("[CNAM consultation card]", {
      patientHasCnam,
      doctorHasCnam,
      patientInsurance: insuranceData,
      doctorInsurances: doctorInfo?.insurances,
    });
  }

  const [medications, setMedications] = useState([]);

  const [medicationsTouched, setMedicationsTouched] =
    useState(false);
  const [loadingCurrentTreatment, setLoadingCurrentTreatment] =
    useState(false);
  const [loadingPreviousPrescription, setLoadingPreviousPrescription] =
    useState(false);

  /* =======================================================
     LAB
  ======================================================= */

  const [selectedLabTests, setSelectedLabTests] =
    useState([
      "creatinine_dfg",
      "nfs",
      "ionogramme",
      "hba1c",
    ]);

  /* =======================================================
     CUSTOM LAB
  ======================================================= */

  const [showCustomLabInput, setShowCustomLabInput] =
    useState(false);

  const [customLabInput, setCustomLabInput] =
    useState("");

  const [customLabTests, setCustomLabTests] =
    useState([]);

  /* =======================================================
     RESOLVE CURRENT TREATMENTS
  ======================================================= */

  const resolvedCurrentTreatments = useMemo(() => {
    const candidates = [
      currentTreatments,

      patientData?.currentTreatments,

      patientData?.treatments,

      patientData?.currentMedications,

      patientData?.medications,

      patientData?.medicalTreatments,
    ];

    const found = candidates.find((candidate) =>
      Array.isArray(candidate)
    );

    return found || [];
  }, [currentTreatments, patientData]);

  /* =======================================================
     INITIAL DOCUMENTS
  ======================================================= */

  useEffect(() => {
    if (!initialDocumentTexts) return;

    setDocumentTexts((previous) => {
      const next = {
        ...previous,
        ...initialDocumentTexts,
      };

      return documentTextsAreEqual(previous, next)
        ? previous
        : next;
    });
  }, [initialDocumentTexts]);

  /* =======================================================
     MEDICATIONS -> PRESCRIPTION
  ======================================================= */

  useEffect(() => {
    if (!medicationsTouched) return;

    const prescriptionText = medications
      .map((medication) => {
        const dosage = medication.dosage
          ? ` — ${medication.dosage}`
          : "";

        return `${medication.name}${dosage}`;
      })
      .join("\n");

    setDocumentTexts((previous) => ({
      ...previous,
      prescription: prescriptionText,
    }));

    if (medications.length === 0) {
      onClearGeneratedDocument?.("prescription");
    }
  }, [
    medications,
    medicationsTouched,
    onClearGeneratedDocument,
  ]);

  /* =======================================================
     LAB -> DOCUMENT
  ======================================================= */

  useEffect(() => {
    const standardLabels = LAB_TEST_OPTIONS
      .filter((test) =>
        selectedLabTests.includes(test.id)
      )
      .map((test) =>
        t(`treatmentPlan.lab.tests.${test.id}`, {
          defaultValue: test.label,
        })
      );

    const customLabels = customLabTests
      .filter((test) => test.checked)
      .map((test) => test.label);

    const allTests = [
      ...standardLabels,
      ...customLabels,
    ];

    const labText =
      allTests.length > 0
        ? [
            t("treatmentPlan.lab.requestTitle"),
            "",
            ...allTests.map(
              (label) => `• ${label}`
            ),
          ].join("\n")
        : "";

    setDocumentTexts((previous) => ({
      ...previous,
      bilan: labText,
    }));

    if (allTests.length === 0) {
      onClearGeneratedDocument?.("bilan");
    }
  }, [
    selectedLabTests,
    customLabTests,
    onClearGeneratedDocument,
    t,
  ]);

  /* =======================================================
     LIVE PREVIEW
  ======================================================= */

  useEffect(() => {
    onDocumentTextsChange?.(documentTexts);

    onPreviewChange?.(
      documentTexts[selectedDocKey] || ""
    );

    onDocKeyChange?.(selectedDocKey);
  }, [
    documentTexts,
    onDocumentTextsChange,
    selectedDocKey,
    onPreviewChange,
    onDocKeyChange,
  ]);

  /* =======================================================
     ACTIVE DOCUMENT CONFIG
  ======================================================= */

  const activeDocument = useMemo(
    () => {
      const document = DOCUMENT_TYPES.find(
        (document) =>
          document.key === selectedDocKey
      );
      return document
        ? { ...document, label: t(`treatmentPlan.doc.types.${document.key}`) }
        : null;
    },
    [selectedDocKey, t]
  );

  const ActiveDocumentIcon =
    activeDocument?.icon || FileText;

  /* =======================================================
     MEDICAL ACT SEARCH
  ======================================================= */

  /*
   * Quand le KC par défaut du médecin change (ex. chargement du profil),
   * on ne met à jour que les actes dont le KC n'a PAS été modifié
   * manuellement : le choix du médecin n'est jamais écrasé.
   */
  useEffect(() => {
    setSelectedActs((previous) =>
      previous.map((act) =>
        act.keyManual
          ? act
          : {
              ...act,
              key: doctorKc,
            }
      )
    );
  }, [doctorKc]);

  const filteredActs = useMemo(() => {
    const query = actQuery
      .trim()
      .toLowerCase();

    if (!query) return [];

    return actCatalog
      .filter(
        (act) =>
          !selectedActs.some(
            (selected) =>
              selected.id === act.id
          )
      )
      .filter((act) => {
        return (
          act.code
            .toLowerCase()
            .includes(query) ||
          act.designation
            .toLowerCase()
            .includes(query) ||
          String(act.key)
            .toLowerCase()
            .includes(query)
        );
      })
      .slice(0, 6);
  }, [actCatalog, actQuery, selectedActs]);

  /* =======================================================
     MEDICAL ACT TOTAL
     total = somme( KC de l'acte x cotation )
  ======================================================= */

  const medicalActsTotal = useMemo(() => {
    return selectedActs.reduce(
      (total, act) => total + getActAmount(act),
      0
    );
  }, [selectedActs]);

  const finalTotal = Number(consultationPrice || 0) + medicalActsTotal;

  useEffect(() => {
    onMedicalActsChange?.({
      medicalActs: selectedActs.map((act) => ({
        ...act,
        key: getActKey(act),
      })),
      medicalActsTotal,
    });
  }, [selectedActs, medicalActsTotal, onMedicalActsChange]);

  useEffect(() => {
    onConsultationPriceChange?.(Number(consultationPrice || 0));
  }, [consultationPrice, onConsultationPriceChange]);

  /* =======================================================
     MEDICAL ACT HANDLERS
  ======================================================= */

  const handleAddAct = (act) => {
    setSelectedActs((previous) => [
      ...previous,
      {
        ...act,
        key: doctorKc,
        keyManual: false,
        cotation: Number(act.cotation) || 0,
      },
    ]);

    setActQuery("");
    setFocusedActSearch(false);
  };

  const handleRemoveAct = (actId) => {
    setSelectedActs((previous) =>
      previous.filter(
        (act) => act.id !== actId
      )
    );
  };

  /* Le médecin modifie le KC d'un acte (1, 2, 3, ...) */
  const handleChangeActKey = (actId, value) => {
    const clean = String(value ?? "").replace(/[^\d.,]/g, "");

    setSelectedActs((previous) =>
      previous.map((act) =>
        act.id === actId
          ? {
              ...act,
              key: clean,
              keyManual: true,
            }
          : act
      )
    );
  };

  /* Si le champ est vide / invalide en quittant l'input : retour au KC du médecin */
  const handleBlurActKey = (actId) => {
    setSelectedActs((previous) =>
      previous.map((act) => {
        if (act.id !== actId) return act;

        const value = Number(
          String(act.key ?? "").replace(",", ".")
        );

        if (Number.isFinite(value) && value > 0) {
          return { ...act, key: value };
        }

        return {
          ...act,
          key: doctorKc,
          keyManual: false,
        };
      })
    );
  };

  /* =======================================================
     ADD MEDICATION
  ======================================================= */

  const handleAddMedication = () => {
    const cleanName =
      medicationName.trim();

    const cleanDosage =
      medicationDosage.trim();

    if (!cleanName && !cleanDosage) {
      return;
    }

    setMedicationsTouched(true);

    setMedications((previous) => [
      ...previous,
      {
        id: `med-${Date.now()}-${Math.random()}`,
        name: cleanName || "Medication",
        dosage: cleanDosage,
      },
    ]);

    setMedicationName("");
    setMedicationDosage("");
  };

  /* =======================================================
     REMOVE MEDICATION
  ======================================================= */

  const handleRemoveMedication = (
    medicationId
  ) => {
    setMedicationsTouched(true);

    setMedications((previous) =>
      previous.filter(
        (medication) =>
          medication.id !== medicationId
      )
    );
  };

  /* =======================================================
     RENEW CURRENT TREATMENT
  ======================================================= */

  const handleRenewCurrentTreatment = async () => {
    if (!patientId) {
      window.alert(t("treatmentPlan.messages.patientMissing"));
      return;
    }

    setLoadingCurrentTreatment(true);
    try {
      let treatments = resolvedCurrentTreatments;
      if (treatments.length === 0) {
        const response = await dispatch(fetchMedications({ userId: patientId })).unwrap();
        treatments = Array.isArray(response)
          ? response
          : response?.medications || response?.data?.medications || response?.data || [];
      }

      const activeTreatments = treatments
        .filter((treatment) => treatment && treatment.isArchived !== true)
        .sort((first, second) => {
          const firstDate = new Date(
            first.updatedAt || first.startDate || first.createdAt || 0
          ).getTime();
          const secondDate = new Date(
            second.updatedAt || second.startDate || second.createdAt || 0
          ).getTime();
          return secondDate - firstDate;
        });
      const renewed = activeTreatments
        .map((treatment, index) => normalizeTreatment(treatment, index))
        .find(Boolean);

      if (!renewed) {
        window.alert(t("treatmentPlan.messages.noCurrentTreatment"));
        return;
      }

      setMedications([renewed]);
      setMedicationsTouched(true);
      setSelectedDocKey("prescription");
    } catch (error) {
      window.alert(error?.message || t("treatmentPlan.messages.currentTreatmentFailed"));
    } finally {
      setLoadingCurrentTreatment(false);
    }
  };

  const handleLoadPreviousPrescription = async () => {
    if (!patientId || !doctorId) {
      window.alert(t("treatmentPlan.messages.patientOrDoctorMissing"));
      return;
    }

    setLoadingPreviousPrescription(true);
    try {
      const response = await dispatch(
        fetchPrescriptionsByPatientForDoctor({ doctorId, patientId })
      ).unwrap();
      const prescriptions = Array.isArray(response)
        ? response
        : response?.documents || response?.data?.documents || response?.data || [];
      const latestPrescription = [...prescriptions].sort((first, second) => {
        const firstDate = new Date(first.updatedAt || first.createdAt || 0).getTime();
        const secondDate = new Date(second.updatedAt || second.createdAt || 0).getTime();
        return secondDate - firstDate;
      })[0];

      if (!latestPrescription) {
        window.alert(t("treatmentPlan.messages.noPreviousPrescription"));
        return;
      }

      const prescriptionText = String(
        latestPrescription.description ||
          latestPrescription.content ||
          latestPrescription.text ||
          latestPrescription.prescription ||
          ""
      ).trim();
      if (!prescriptionText) {
        window.alert(t("treatmentPlan.messages.emptyPreviousPrescription"));
        return;
      }

      const renewed = prescriptionText
        .split(/\r?\n/)
        .map((line, index) => {
          const cleanLine = line.replace(/^\s*(?:[-•]|\d+[.)])\s*/, "").trim();
          if (!cleanLine) return null;
          const [name, ...dosageParts] = cleanLine.split(/\s+[—–-]\s+/);
          return normalizeTreatment(
            { name, dosage: dosageParts.join(" - ") },
            index
          );
        })
        .filter(Boolean);

      setMedications(renewed);
      setMedicationsTouched(false);
      setDocumentTexts((previous) => ({
        ...previous,
        prescription: prescriptionText,
      }));
      setSelectedDocKey("prescription");
    } catch (error) {
      window.alert(error?.message || t("treatmentPlan.messages.previousPrescriptionFailed"));
    } finally {
      setLoadingPreviousPrescription(false);
    }
  };

  /* =======================================================
     STANDARD LAB
  ======================================================= */

  const handleToggleLabTest = (
    testId
  ) => {
    setSelectedLabTests((previous) => {
      if (previous.includes(testId)) {
        return previous.filter(
          (id) => id !== testId
        );
      }

      return [...previous, testId];
    });
  };

  /* =======================================================
     CUSTOM LAB TOGGLE
  ======================================================= */

  const handleToggleCustomLabTest = (
    testId
  ) => {
    setCustomLabTests((previous) =>
      previous.map((test) =>
        test.id === testId
          ? {
              ...test,
              checked: !test.checked,
            }
          : test
      )
    );
  };

  /* =======================================================
     ADD CUSTOM LAB
  ======================================================= */

  const handleAddCustomLabTest = () => {
    const value =
      customLabInput.trim();

    if (!value) return;

    const alreadyExists =
      customLabTests.some(
        (test) =>
          test.label
            .trim()
            .toLowerCase() ===
          value.toLowerCase()
      );

    const existsDefault =
      LAB_TEST_OPTIONS.some(
        (test) =>
          test.label
            .trim()
            .toLowerCase() ===
          value.toLowerCase()
      );

    if (
      alreadyExists ||
      existsDefault
    ) {
      setCustomLabInput("");
      return;
    }

    setCustomLabTests((previous) => [
      ...previous,
      {
        id: `custom-lab-${Date.now()}-${Math.random()}`,
        label: value,
        checked: true,
      },
    ]);

    setCustomLabInput("");
    setShowCustomLabInput(false);
  };

  /* =======================================================
     REMOVE CUSTOM LAB
  ======================================================= */

  const handleRemoveCustomLabTest = (
    testId
  ) => {
    setCustomLabTests((previous) =>
      previous.filter(
        (test) => test.id !== testId
      )
    );
  };

  /* =======================================================
     CUSTOM LAB KEYBOARD
  ======================================================= */

  const handleCustomLabKeyDown = (
    event
  ) => {
    if (event.key === "Enter") {
      event.preventDefault();

      handleAddCustomLabTest();
    }

    if (event.key === "Escape") {
      setCustomLabInput("");
      setShowCustomLabInput(false);
    }
  };

  /* =======================================================
     CERTIFICATE / LETTER
  ======================================================= */

  const handleDocumentChange = (
    value
  ) => {
    const nextValue =
      String(value || "");

    setDocumentTexts((previous) => ({
      ...previous,
      [selectedDocKey]: nextValue,
    }));

    if (!nextValue.trim()) {
      onClearGeneratedDocument?.(
        selectedDocKey
      );
    }
  };

  const handleDocumentTabChange = (key) => {
    setSelectedDocKey(key);
    onClearGeneratedDocument?.(key);
  };

  /* =======================================================
     GENERATE
  ======================================================= */

  const handleGenerate = () => {
    const content = String(
      documentTexts[selectedDocKey] || ""
    ).trim();

    if (!content) {
      console.warn(
        `No content for ${selectedDocKey}`
      );

      return;
    }

    const generatedDocument = {
      type: selectedDocKey,

      title:
        activeDocument?.label ||
        selectedDocKey,

      content,

      metadata: {
        medications:
          selectedDocKey === "prescription"
            ? medications
            : [],

        labTests:
          selectedDocKey === "bilan"
            ? [
                ...selectedLabTests,

                ...customLabTests
                  .filter(
                    (test) =>
                      test.checked
                  )
                  .map(
                    (test) =>
                      test.id
                  ),
              ]
            : [],

        customLabTests:
          selectedDocKey === "bilan"
            ? customLabTests.filter(
                (test) =>
                  test.checked
              )
            : [],

        medicalActs:
          selectedActs.map((act) => ({
            ...act,
            key: getActKey(act),
          })),
      },
    };

    onGenerateDocument?.(
      generatedDocument
    );
  };

  /* =======================================================
     UI
  ======================================================= */

  return (
    <div className="tp-figma">
      {/* =================================================
          MEDICAL ACTS
      ================================================= */}

      <section className="tp-figma-card tp-medical-card">
        <div className="tp-medical-head">
          <div>
            <h3>
              {t("treatmentPlan.medicalActs.title")}
            </h3>

            <p>
              {t("treatmentPlan.medicalActs.subtitle")}
            </p>
          </div>

          <div className="tp-total-box">
            <span>
              {t("treatmentPlan.medicalActs.total")}
            </span>

            <strong>
              {finalTotal.toFixed(2)} DT
            </strong>
          </div>
        </div>



        <label className="tp-small-label">
          {t("treatmentPlan.medicalActs.searchLabel")}
        </label>

        <div className="tp-search-wrapper">
          <div
            className={`tp-search ${
              focusedActSearch
                ? "focused"
                : ""
            }`}
          >
            <input
              type="text"
              value={actQuery}
              placeholder={t("treatmentPlan.medicalActs.searchPlaceholder")}
              onFocus={() =>
                setFocusedActSearch(
                  true
                )
              }
              onChange={(event) => {
                setActQuery(
                  event.target.value
                );

                setFocusedActSearch(
                  true
                );
              }}
            />

            <Search size={15} />
          </div>

          {focusedActSearch &&
            filteredActs.length > 0 && (
              <div className="tp-search-dropdown">
                {filteredActs.map((act) => (
                  <button
                    key={act.id}
                    type="button"
                    onClick={() =>
                      handleAddAct(act)
                    }
                  >
                    <div>
                      <strong>
                        {act.code}
                      </strong>

                      <span>
                        {
                          act.designation
                        }
                      </span>
                    </div>

                    <span className="tp-result-price">
                      {act.keyLabel} {doctorKc} x {act.cotation} DT
                    </span>
                  </button>
                ))}
              </div>
            )}
        </div>

        {/* =================================================
            SELECTED ACTS
        ================================================= */}

        {selectedActs.length > 0 && (
          <div className="tp-selected-acts">
            {selectedActs.map((act) => (
              <div
                key={act.id}
                className="tp-selected-act"
              >
                <div className="tp-act-main">
                  <b>{act.code}</b>

                  <span>
                    {act.designation}
                  </span>
                </div>

                {/* ✅ KC MODIFIABLE PAR LE MÉDECIN */}

                <label
                  className="tp-act-key"
                  title={t("treatmentPlan.medicalActs.coefficientTitle")}
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 4,
                  }}
                >
                  <span>{act.keyLabel || "KC"}</span>

                  <input
                    type="number"
                    inputMode="decimal"
                    min="1"
                    step="1"
                    value={act.key ?? ""}
                    onChange={(event) =>
                      handleChangeActKey(
                        act.id,
                        event.target.value
                      )
                    }
                    onBlur={() =>
                      handleBlurActKey(act.id)
                    }
                    aria-label={t("treatmentPlan.medicalActs.coefficientLabel", { coefficient: act.keyLabel || "KC", code: act.code })}
                    style={{
                      width: 46,
                      height: 26,
                      padding: "0 6px",
                      border: "1px solid #cbd5e1",
                      borderRadius: 6,
                      background: "#fff",
                      fontSize: 12,
                      fontWeight: 600,
                      textAlign: "center",
                    }}
                  />
                </label>

                <label className="tp-act-price">
                  <span>{t("treatmentPlan.medicalActs.fee")}</span>
                  <strong>{Number(act.cotation || 0).toFixed(2)} DT</strong>
                  {getActKey(act) !== 1 && (
                    <small
                      style={{
                        display: "block",
                        fontSize: 10,
                        opacity: 0.7,
                      }}
                    >
                      = {getActAmount(act).toFixed(2)} DT
                    </small>
                  )}
                </label>

                {/* ✅ STYLED DELETE BUTTON */}

                <button
                  type="button"
                  className="tp-act-delete"
                  onClick={() =>
                    handleRemoveAct(
                      act.id
                    )
                  }
                  aria-label={t("treatmentPlan.medicalActs.remove")}
                  title={t("treatmentPlan.medicalActs.remove")}
                >
                  <Trash2
                    size={14}
                    strokeWidth={1.7}
                  />
                </button>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* =================================================
          ISSUE DOCUMENT
      ================================================= */}

      <section className="tp-figma-card tp-document-card">
        <div className="tp-document-heading">
          <h3>
            {t("treatmentPlan.issueDocument")}
          </h3>

          <p>
            {t("treatmentPlan.issueDocumentSubtitle")}
          </p>
        </div>

        {/* =================================================
            DOCUMENT TABS
        ================================================= */}

        <div className="tp-document-tabs">
          {DOCUMENT_TYPES.map(
            (document) => {
              const Icon =
                document.icon;

              return (
                <button
                  key={document.key}
                  type="button"
                  className={
                    selectedDocKey ===
                    document.key
                      ? "active"
                      : ""
                  }
                  onClick={() =>
                    handleDocumentTabChange(
                      document.key
                    )
                  }
                >
                  <Icon
                    size={12}
                    strokeWidth={1.8}
                  />

                  <span>
                    {t(`treatmentPlan.doc.types.${document.key}`)}
                  </span>
                </button>
              );
            }
          )}
        </div>

        {/* =================================================
            PRESCRIPTION
        ================================================= */}

        {selectedDocKey ===
        "prescription" ? (
          <div className="tp-document-inner tp-prescription-panel">
            <div className="tp-document-toolbar tp-prescription-toolbar">
              <div className="tp-prescription-heading">
                <h5>
                  {t("treatmentPlan.prescription.title")}
                </h5>

                <p>
                  {t("treatmentPlan.prescription.interactions")}
                </p>
              </div>

              <div className="tp-prescription-dictation">
                <span className="tp-dictation-label">
                  <span className="tp-status-dot" />

                  {t("treatmentPlan.dictation")}
                </span>

                <span className="tp-prescription-wave">
                  <span />
                </span>

                <button
                  type="button"
                  className="tp-mic-button"
                >
                  <Mic size={14} />
                </button>
              </div>
            </div>

            {/* ACTIONS */}

            <div className="tp-prescription-actions">
              <button
                type="button"
                className="tp-renew-treatment"
                onClick={
                  handleRenewCurrentTreatment
                }
                disabled={loadingCurrentTreatment}
              >
                <RefreshCw
                  size={12}
                  strokeWidth={1.8}
                />

                  {loadingCurrentTreatment ? t("treatmentPlan.actions.loading") : t("treatmentPlan.prescription.renewCurrent")}
              </button>

              <button
                type="button"
                className="tp-previous-prescriptions"
                onClick={handleLoadPreviousPrescription}
                disabled={loadingPreviousPrescription}
              >
                <Copy
                  size={12}
                  strokeWidth={1.8}
                />

                {loadingPreviousPrescription ? t("treatmentPlan.actions.loading") : t("treatmentPlan.prescription.previous")}
              </button>
            </div>

            {/* MEDICATIONS */}

            <div className="tp-medication-area">
              {medications.length === 0 ? (
                <div className="tp-no-medication">
                  <span>•</span>

                  {t("treatmentPlan.prescription.noMedication")}
                </div>
              ) : (
                <div className="tp-medications-list">
                  {medications.map(
                    (medication) => (
                      <div
                        key={medication.id}
                        className="tp-medication-row"
                      >
                        <div className="tp-medication-data">
                          <strong>
                            {
                              medication.name
                            }
                          </strong>

                          {medication.dosage && (
                            <span>
                              {
                                medication.dosage
                              }
                            </span>
                          )}
                        </div>

                        <button
                          type="button"
                          className="tp-medication-remove"
                          onClick={() =>
                            handleRemoveMedication(
                              medication.id
                            )
                          }
                        >
                          <Trash2
                            size={12}
                          />
                        </button>
                      </div>
                    )
                  )}
                </div>
              )}

              <div className="tp-prescription-input-row">
                <input
                  type="text"
                  value={medicationName}
                  onChange={(event) =>
                    setMedicationName(
                      event.target.value
                    )
                  }
                  placeholder={t("treatmentPlan.prescription.namePlaceholder")}
                />

                <input
                  type="text"
                  value={
                    medicationDosage
                  }
                  onChange={(event) =>
                    setMedicationDosage(
                      event.target.value
                    )
                  }
                  placeholder={t("treatmentPlan.prescription.dosagePlaceholder")}
                />

                <button
                  type="button"
                  className="tp-add-medication"
                  onClick={
                    handleAddMedication
                  }
                >
                  <Plus size={14} />

                  {t("treatmentPlan.actions.add")}
                </button>
              </div>
            </div>

          </div>
        ) : selectedDocKey === "bilan" ? (
          /* =================================================
             LAB
          ================================================= */

          <div className="tp-document-inner tp-lab-panel">
            <div className="tp-document-toolbar tp-lab-toolbar">
              <div>
                <h5 className="tp-lab-title">
                  {t("treatmentPlan.lab.title")}
                </h5>

                <p>
                  {t("treatmentPlan.prescription.interactions")}
                </p>
              </div>

              <div className="tp-dictation">
                <span className="tp-dictation-label">
                  <span className="tp-status-dot" />

                  {t("treatmentPlan.dictation")}
                </span>

                <span className="tp-lab-wave">
                  <span />
                </span>

                <button
                  type="button"
                  className="tp-mic-button"
                >
                  <Mic size={15} />
                </button>
              </div>
            </div>

            <p className="tp-lab-helper">
              {t("treatmentPlan.lab.suggested")}
            </p>

            <div className="tp-lab-tests-grid">
              {/* STANDARD */}

              {LAB_TEST_OPTIONS.map(
                (test) => {
                  const checked =
                    selectedLabTests.includes(
                      test.id
                    );

                  return (
                    <label
                      key={test.id}
                      className={`tp-lab-test-item ${
                        checked
                          ? "checked"
                          : ""
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() =>
                          handleToggleLabTest(
                            test.id
                          )
                        }
                      />

                      <span className="tp-lab-checkbox">
                        {checked
                          ? "✓"
                          : ""}
                      </span>

                      <span className="tp-lab-test-label">
                        {t(`treatmentPlan.lab.tests.${test.id}`, { defaultValue: test.label })}
                      </span>
                    </label>
                  );
                }
              )}

              {/* CUSTOM */}

              {customLabTests.map(
                (test) => {
                  const checked =
                    Boolean(test.checked);

                  return (
                    <div
                      key={test.id}
                      className="tp-custom-lab-grid-item"
                    >
                      <label
                        className={`tp-lab-test-item ${
                          checked
                            ? "checked"
                            : ""
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() =>
                            handleToggleCustomLabTest(
                              test.id
                            )
                          }
                        />

                        <span className="tp-lab-checkbox">
                          {checked
                            ? "✓"
                            : ""}
                        </span>

                        <span className="tp-lab-test-label">
                          {t(`treatmentPlan.lab.tests.${test.id}`, { defaultValue: test.label })}
                        </span>
                      </label>

                      <button
                        type="button"
                        className="tp-custom-lab-delete"
                        onClick={() =>
                          handleRemoveCustomLabTest(
                            test.id
                          )
                        }
                      >
                        <X size={11} />
                      </button>
                    </div>
                  );
                }
              )}
            </div>

            {/* OTHER ANALYSIS */}

            {!showCustomLabInput ? (
              <button
                type="button"
                className="tp-other-analysis-btn"
                onClick={() =>
                  setShowCustomLabInput(
                    true
                  )
                }
              >
                <Plus size={13} />

                {t("treatmentPlan.lab.otherAnalysis")}
              </button>
            ) : (
              <div className="tp-custom-lab-input-row">
                <input
                  autoFocus
                  type="text"
                  value={customLabInput}
                  onChange={(event) =>
                    setCustomLabInput(
                      event.target.value
                    )
                  }
                  onKeyDown={
                    handleCustomLabKeyDown
                  }
                  placeholder={t("treatmentPlan.lab.customPlaceholder")}
                />

                <button
                  type="button"
                  className="tp-custom-lab-add"
                  onClick={
                    handleAddCustomLabTest
                  }
                >
                  <Plus size={13} />

                  {t("treatmentPlan.actions.add")}
                </button>

                <button
                  type="button"
                  className="tp-custom-lab-cancel"
                  onClick={() => {
                    setCustomLabInput("");

                    setShowCustomLabInput(
                      false
                    );
                  }}
                >
                  <X size={13} />
                </button>
              </div>
            )}

          </div>
        ) : (
          /* =================================================
             CERTIFICATE / LETTER
          ================================================= */

          <div className="tp-document-inner tp-generic-document">
            <div className="tp-document-toolbar">
              <div className="tp-document-toolbar-title">
                <ActiveDocumentIcon
                  size={13}
                  strokeWidth={1.8}
                />

                <div>
                  <h5>
                    {t(`treatmentPlan.doc.types.${selectedDocKey}`)}
                  </h5>

                  <p>
                    {t("treatmentPlan.document.completeInfo")}
                  </p>
                </div>
              </div>

              <div className="tp-dictation">
                <span className="tp-dictation-label">
                  <span className="tp-status-dot" />

                  {t("treatmentPlan.dictation")}
                </span>

                <span className="tp-lab-wave">
                  <span />
                </span>

                <button
                  type="button"
                  className="tp-mic-button"
                >
                  <Mic size={15} />
                </button>
              </div>
            </div>

            <textarea
              key={selectedDocKey}
              className="tp-document-textarea"
              value={
                documentTexts[
                  selectedDocKey
                ] || ""
              }
              onChange={(event) =>
                handleDocumentChange(
                  event.target.value
                )
              }
              placeholder={t(`treatmentPlan.doc.placeholders.${selectedDocKey}`)}
            />

          </div>
        )}
      </section>

      {/* =================================================
          INSURANCE
      ================================================= */}

      {shouldDisplayInsuranceCard && (
        <section className="tp-insurance-card">
            <div className="tp-insurance-check">
              ✓
            </div>

            <div className="tp-insurance-info">
              <strong>
                {t("treatmentPlan.insurance.title")}
              </strong>

              <span>
                {insuranceData?.provider ||
                  t("treatmentPlan.insurance.providerFallback")}
              </span>
            </div>

            <span className="tp-insurance-status">
              {t("treatmentPlan.insurance.activeFullCoverage")}
            </span>

            <button
              type="button"
              className="tp-insurance-btn"
              onClick={() => setIsCnamBookOpen(true)}
            >
              {t("treatmentPlan.insurance.viewDocument")}
            </button>
        </section>
      )}

      <CnamBookModal
        open={isCnamBookOpen}
        onClose={() => setIsCnamBookOpen(false)}
        userId={patientId}
        readOnly
        hideSaveButton
      />
    </div>
  );
}